import { useCallback } from 'react';
import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { customTravelFormStateAtom } from '@/atoms/customTravelFormState';
import { CustomTravelEstimatePopupStep, CustomTravelParamType } from '@/components/CustomTravel/EstimatePopup';
import { CustomTravelFormState } from '@/atoms/customTravelFormState';

const usePopupHandler = () => {
  const router = useRouter();
  const { agtName, productId } = useParams();
  const searchParams = useSearchParams();

  const pathname = usePathname();

  const setCustomTravelFormState = useSetAtom(customTravelFormStateAtom);

  const currentStep = searchParams.get('step') as CustomTravelEstimatePopupStep;
  const currentType = searchParams.get('type') as CustomTravelParamType;

  const setStep = (step: CustomTravelEstimatePopupStep, searchParams?: { type: CustomTravelParamType }) => {
    const searchParamStr = Object.entries({ ...searchParams, step })
      .filter(([, value]) => value !== null)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    router.replace(`${pathname}?${searchParamStr}`);
  };

  const handleBack = (step: CustomTravelEstimatePopupStep, searchParams?: { type: CustomTravelParamType }) => () => {
    switch (currentType) {
      case CustomTravelParamType.CHECK_ESTIMATE:
        handleClose();
        break;

      default:
        if (step) {
          setStep(step, searchParams);
        }
        break;
    }
  };

  const onSubmitInquiry = (values: CustomTravelFormState) => {
    setCustomTravelFormState((prev) => ({ ...prev, ...values }));
    router.push(`/custom-travel/${agtName}/${productId}/consulting`);
  };

  const onSubmitConfirm = (values: CustomTravelFormState) => {
    switch (currentType) {
      case CustomTravelParamType.CHECK_ESTIMATE:
        onSubmitConfirmReplace(values);
        break;

      default:
        setCustomTravelFormState((prev) => ({ ...prev, ...values }));
        router.push(`/custom-travel/${agtName}/${productId}/estimate`);
        break;
    }
  };

  const onSubmitConfirmReplace = (values: CustomTravelFormState) => {
    setCustomTravelFormState((prev) => ({ ...prev, ...values }));
    router.replace(`/custom-travel/${agtName}/${productId}/estimate`);
  };

  const onSubmitNextStep = (
    values: CustomTravelFormState,
    step: CustomTravelEstimatePopupStep,
    searchParams?: { type: CustomTravelParamType },
  ) => {
    switch (currentType) {
      case CustomTravelParamType.CHECK_ESTIMATE:
        onSubmitConfirmReplace(values);
        break;

      default:
        setCustomTravelFormState((prev) => ({ ...prev, ...values }));
        setStep(step, searchParams);
        break;
    }
  };

  const handleClose = useCallback(() => {
    router.replace(pathname);
  }, [router, pathname]);

  return {
    currentStep,
    currentType,
    handleBack,
    onSubmitNextStep,
    onSubmitConfirm,
    onSubmitInquiry,
    handleClose,
  };
};

export default usePopupHandler;
